import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ArrowUp, ArrowDown, Trash2, Plus, Save, Upload, Loader2, X, Monitor, Smartphone, Tablet } from "lucide-react";
import { toast } from "sonner";
import { SlideView, DESIGN } from "@/components/SlideView";
import { SlideDesign } from "./SlideDesign";

const nid = () => Math.random().toString(36).slice(2, 12);
const BLANK = { enabled: true, eyebrow: "", heading: "New slide heading", text: "", button_label: "", button_link: "", image_url: "", badges: [], design: { ...DESIGN } };

function Preview({ s, i }) {
  const [dev, setDev] = useState("desktop");
  const box = { desktop: "w-full aspect-[1600/460]", tablet: "w-[240px] aspect-[768/420]", mobile: "w-[150px] aspect-[390/400]" }[dev];
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between"><Label className="text-xs">Live preview</Label>
        <div className="inline-flex rounded border">
          {[["desktop", Monitor], ["tablet", Tablet], ["mobile", Smartphone]].map(([k, Icon]) => (
            <button key={k} type="button" onClick={() => setDev(k)} aria-label={`${k} preview`} className={`p-1 ${dev === k ? "bg-steel text-white" : ""}`} data-testid={`slide-preview-${k}-${i}`}><Icon size={13} /></button>))}
        </div></div>
      <div className={`relative overflow-hidden rounded-lg bg-steel text-white mx-auto ${box}`} data-testid={`slide-preview-${i}`}>
        <SlideView s={s} mini device={dev} />
      </div>
    </div>
  );
}

function Slide({ s, i, set, remove, up, down }) {
  const [busy, setBusy] = useState(false);
  const f = (k, v) => set({ ...s, [k]: v });
  const d = { ...DESIGN, ...(s.design || {}) };
  const upload = async (e, key = "image_url") => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); fd.append("kind", "image");
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set({ ...s, [key]: data.url, design: { ...d, bg_type: "image" } }); toast.success("Picture uploaded"); }
    catch (er) { toast.error(er.response?.data?.detail || "Upload failed"); } finally { setBusy(false); }
  };
  const badges = [0, 1, 2].map((k) => s.badges[k] || "");
  return (
    <div className="rounded-xl border bg-white p-5 space-y-5" data-testid={`slide-${i}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <b className="text-steel">Slide {i + 1}</b>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm"><Switch checked={s.enabled} onCheckedChange={(v) => f("enabled", v)} data-testid={`slide-enabled-${i}`} /> Show</label>
          <button onClick={up}><ArrowUp size={16} /></button><button onClick={down}><ArrowDown size={16} /></button>
          <button onClick={remove} className="text-red-600" data-testid={`slide-remove-${i}`}><Trash2 size={16} /></button></div>
      </div>
      <div className="grid lg:grid-cols-[1fr_1fr] gap-5">
        <div className="space-y-3">
          <Preview s={{ ...s, design: d }} i={i} />
          <div className="flex flex-wrap gap-2">
            <label className="flex items-center gap-2 h-9 px-3 rounded-md border text-sm cursor-pointer hover:bg-slate-50">
              <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`slide-upload-${i}`} />
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {s.image_url ? "Replace picture" : "Upload picture"}</label>
            {s.image_url && <Button variant="outline" size="sm" className="h-9 text-red-600" onClick={() => f("image_url", "")} data-testid={`slide-remove-image-${i}`}><X size={14} className="mr-1" /> Remove picture</Button>}
          </div>
          <Input value={s.image_url} onChange={(e) => f("image_url", e.target.value)} placeholder="or paste an https:// image URL" className="h-8 text-xs" data-testid={`slide-image-${i}`} />
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <label className="flex items-center gap-2 h-9 px-3 rounded-md border text-sm cursor-pointer hover:bg-slate-50">
              <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e, "mobile_image_url")} data-testid={`slide-upload-mobile-${i}`} />
              <Smartphone size={15} /> {s.mobile_image_url ? "Replace phone picture" : "Add phone picture (optional)"}</label>
            {s.mobile_image_url && <Button variant="outline" size="sm" className="h-9 text-red-600" onClick={() => f("mobile_image_url", "")} data-testid={`slide-remove-mobile-image-${i}`}><X size={14} className="mr-1" /> Remove phone picture</Button>}
          </div>
          <p className="text-xs text-muted-foreground">Phone picture is shown on screens under 640 px wide — e.g. a 780 × 800 px version of your banner.</p>
          <p className="text-xs text-muted-foreground">Ready-made banners: about 1600 × 460 px for desktop. Use "Show whole picture" so nothing is cropped.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 content-start">
          <div className="sm:col-span-2"><Label className="text-xs">Small top line</Label><Input value={s.eyebrow} onChange={(e) => f("eyebrow", e.target.value)} data-testid={`slide-eyebrow-${i}`} /></div>
          <div className="sm:col-span-2"><Label className="text-xs">Heading{d.show_text ? " *" : " (used as picture description)"}</Label><Input value={s.heading} onChange={(e) => f("heading", e.target.value)} data-testid={`slide-heading-${i}`} /></div>
          <div className="sm:col-span-2"><Label className="text-xs">Text</Label><Textarea rows={2} value={s.text} onChange={(e) => f("text", e.target.value)} data-testid={`slide-text-${i}`} /></div>
          <div><Label className="text-xs">Button label</Label><Input value={s.button_label} onChange={(e) => f("button_label", e.target.value)} placeholder="e.g. Shop POS" data-testid={`slide-btn-label-${i}`} /></div>
          <div><Label className="text-xs">Button / slide link</Label><Input value={s.button_link} onChange={(e) => f("button_link", e.target.value)} placeholder="/category/pos-systems" data-testid={`slide-btn-link-${i}`} /></div>
          {badges.map((b, k) => (
            <div key={k} className={k === 2 ? "sm:col-span-2" : ""}><Label className="text-xs">Feature badge {k + 1}</Label>
              <Input value={b} onChange={(e) => { const nb = [...badges]; nb[k] = e.target.value; f("badges", nb); }} data-testid={`slide-badge-${i}-${k}`} /></div>))}
        </div>
      </div>
      <div className="border-t pt-4"><SlideDesign d={d} set={(v) => f("design", v)} i={i} /></div>
    </div>
  );
}

export default function SlidesTab() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/site/slides").then((r) => setD(r.data)); }, []);
  if (!d) return <p className="text-muted-foreground">Loading…</p>;
  const setSlides = (slides) => setD({ ...d, slides });
  const mv = (i, dir) => { const a = [...d.slides]; const j = i + dir; if (j < 0 || j >= a.length) return; [a[i], a[j]] = [a[j], a[i]]; setSlides(a); };
  const save = async () => {
    try { const r = await api.put("/admin/site/slides", { ...d, slides: d.slides.map((s) => ({ ...s, badges: s.badges.filter(Boolean) })) }); setD(r.data); toast.success("Slides saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="max-w-6xl space-y-4" data-testid="slides-tab">
      <div><h1 className="text-3xl font-extrabold mb-1">Homepage Slides</h1>
        <p className="text-muted-foreground">Rotating banner at the top of the homepage. Each slide can be a ready-made picture, a picture with text, or a coloured background with text.</p></div>
      <div className="flex flex-wrap items-end gap-3 sticky top-0 z-10 bg-background/95 backdrop-blur py-2">
        <Button onClick={save} className="bg-accent text-steel hover:bg-amber-600 font-semibold" data-testid="slides-save"><Save size={16} className="mr-2" /> Save slides</Button>
        <Button variant="outline" onClick={() => setSlides([...d.slides, { ...BLANK, id: nid() }])} data-testid="slides-add"><Plus size={16} className="mr-1" /> Add slide</Button>
        <div><Label className="text-xs">Auto-rotate every (seconds, 0 = off)</Label>
          <Input type="number" min="0" max="30" value={d.autoplay_seconds} onChange={(e) => setD({ ...d, autoplay_seconds: e.target.value })} className="h-9 w-28" data-testid="slides-autoplay" /></div>
      </div>
      {d.slides.map((s, i) => (
        <Slide key={s.id} s={s} i={i} set={(v) => setSlides(d.slides.map((x, n) => (n === i ? v : x)))}
          remove={() => window.confirm("Delete this slide?") && setSlides(d.slides.filter((_, n) => n !== i))}
          up={() => mv(i, -1)} down={() => mv(i, 1)} />))}
    </div>
  );
}
